import { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  Fab,
  Chip,
  TextField,
  InputAdornment,
  Divider,
  ButtonBase
} from "@mui/material";
import Add from "@mui/icons-material/Add";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import SearchRounded from "@mui/icons-material/SearchRounded";
import ScienceOutlined from "@mui/icons-material/ScienceOutlined";

import { TESTING_SESSIONS } from "@/data/mock-data";
import type { TestingSession } from "@/data/mock-data";
import { formatPct } from "@/lib/formatters";
import { BotsMarketplaceToggle } from "@/components/BotsMarketplaceToggle";
import { EmptyState } from "@/components/EmptyState";
import { TestingSessionDrawer } from "@/components/TestingSessionDrawer";

export function TestingPage() {
  const [q, setQ] = useState("");
  // Tapping a row opens the session detail drawer in place.
  const [detail, setDetail] = useState<TestingSession | null>(null);

  const query = q.trim().toLowerCase();
  const sessions = useMemo(
    () =>
      TESTING_SESSIONS.filter(
        (s) =>
          !query ||
          s.botName.toLowerCase().includes(query) ||
          s.portfolio.toLowerCase().includes(query)
      ),
    [query]
  );

  return (
    <Box sx={{ maxWidth: 960, mx: "auto", position: "relative", px: 2, pt: 1 }}>
      <BotsMarketplaceToggle />

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.5 }}
      >
        <Typography variant="overline" color="text.secondary">
          Recent sessions
        </Typography>
        <Button startIcon={<RefreshRounded />} size="small">
          Refresh
        </Button>
      </Stack>

      <TextField
        fullWidth
        size="small"
        placeholder="Search sessions…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ mr: 0.5 }}>
              <SearchRounded sx={{ fontSize: 18, color: "text.secondary" }} />
            </InputAdornment>
          )
        }}
        sx={{
          mb: 1.5,
          "& .MuiOutlinedInput-root": {
            borderRadius: 18,
            fontSize: "1rem",
            height: 36,
            bgcolor: "background.paper"
          },
          "& .MuiOutlinedInput-input": { py: 0.25 }
        }}
      />

      {sessions.length === 0 ? (
        <EmptyState
          icon={<ScienceOutlined />}
          title={query ? "No sessions match your search" : "No backtest sessions"}
          body={
            query
              ? `Nothing found for “${q.trim()}”.`
              : "Run a backtest to simulate a strategy over historical data before going live."
          }
          actions={
            query
              ? [{ label: "Clear search", onClick: () => setQ("") }]
              : []
          }
        />
      ) : (
        <Box>
          {sessions.map((s, i) => (
            <Box key={s.id}>
              {i > 0 && <Divider />}
              <ButtonBase
                onClick={() => setDetail(s)}
                sx={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  py: 1.5,
                  "&:hover": { bgcolor: "action.hover" }
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={700} noWrap>
                      {s.botName}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      sx={{ display: "block" }}
                    >
                      {s.startedAt} · {s.notes}
                    </Typography>
                  </Box>
                  <Stack alignItems="flex-end" spacing={0.5} sx={{ flexShrink: 0 }}>
                    <Typography
                      variant="body2"
                      sx={(t) => ({
                        fontWeight: 700,
                        fontFeatureSettings: '"tnum" 1',
                        color:
                          s.return >= 0
                            ? t.palette.primary.main
                            : t.palette.error.main
                      })}
                    >
                      {formatPct(s.return)}
                    </Typography>
                    <Chip
                      label={`${s.trades} trades`}
                      size="small"
                      variant="outlined"
                    />
                  </Stack>
                </Stack>
              </ButtonBase>
            </Box>
          ))}
        </Box>
      )}

      <TestingSessionDrawer
        session={detail}
        onClose={() => setDetail(null)}
      />

      <Fab
        color="primary"
        variant="extended"
        sx={{
          position: "fixed",
          right: 16,
          bottom: { xs: "calc(80px + env(safe-area-inset-bottom))", md: 24 }
        }}
      >
        <Add sx={{ mr: 1 }} /> New backtest
      </Fab>
    </Box>
  );
}
